import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { CatMark } from "@/components/CatMark";
import LivingDoodles from "@/components/LivingDoodles";

/**
 * IntroHero — cinematic opening sequence for La Michi Agencia.
 * Full-screen editorial collage introducing Michi (elegant black cat).
 * Layered SVG doodles: dotted routes, passport stamps, paw prints, tropical
 * plants, mushroom, rainbow, luggage tag, airplane, handwritten labels.
 * Single interactive CTA "Haz clic para descubrir." — click triggers a
 * signature circular-mask reveal that dissolves the intro into the site.
 */

// ---------- Decorative primitives (inline SVG) ----------

const PawPrint = ({ style = {}, size = 34, rotate = 0, opacity = 1, className = "" }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 40 40"
    style={{ position: "absolute", transform: `rotate(${rotate}deg)`, opacity, ...style }}
    aria-hidden="true"
  >
    <g fill="#111111">
      <ellipse cx="10" cy="14" rx="3" ry="4" />
      <ellipse cx="20" cy="10" rx="3" ry="4" />
      <ellipse cx="30" cy="14" rx="3" ry="4" />
      <ellipse cx="25" cy="21" rx="3" ry="4" />
      <path d="M14 32 C 10 26, 30 26, 26 32 C 24 36, 16 36, 14 32 Z" />
    </g>
  </svg>
);

const PassportStamp = ({ style, label = "MICHI · BCN · 2025", rotate = -8, tint = "#FF6B2C" }) => (
  <svg width="180" height="88" viewBox="0 0 180 88" style={{ position: "absolute", transform: `rotate(${rotate}deg)`, ...style }} aria-hidden="true">
    <g stroke={tint} strokeWidth="1.4" fill="none" opacity="0.85">
      <rect x="6" y="6" width="168" height="76" rx="8" />
      <rect x="10" y="10" width="160" height="68" rx="6" strokeDasharray="4 3" />
      <path d="M20 44 L 60 44" />
      <path d="M120 44 L 160 44" />
      <circle cx="90" cy="44" r="18" />
      <path d="M75 44 L 90 34 L 105 44 L 90 54 Z" />
    </g>
    <text x="90" y="72" textAnchor="middle" fontSize="9" fontFamily="Manrope, sans-serif" fill={tint} letterSpacing="3">
      {label}
    </text>
    <text x="90" y="26" textAnchor="middle" fontSize="8" fontFamily="Manrope, sans-serif" fill={tint} letterSpacing="2" opacity="0.7">
      VISA · APPROVED
    </text>
  </svg>
);

const Rainbow = ({ style }) => (
  <svg width="360" height="200" viewBox="0 0 360 200" style={{ position: "absolute", ...style }} aria-hidden="true">
    {["#FF6B2C", "#F4A261", "#E9C46A", "#8FB98A", "#7BA6C2", "#8A6BA1"].map((c, i) => (
      <path
        key={c}
        d={`M ${20 + i * 6} 190 A ${160 - i * 6} ${160 - i * 6} 0 0 1 ${340 - i * 6} 190`}
        stroke={c}
        strokeWidth="9"
        fill="none"
        strokeLinecap="round"
        opacity="0.85"
      />
    ))}
  </svg>
);

const Airplane = ({ style }) => (
  <svg width="80" height="80" viewBox="0 0 80 80" style={{ position: "absolute", ...style }} aria-hidden="true">
    <g fill="#111111">
      <path d="M12 40 L 60 30 L 68 32 L 66 40 L 60 42 L 12 42 Z" />
      <path d="M28 34 L 40 20 L 44 20 L 40 34 Z" />
      <path d="M28 40 L 40 54 L 44 54 L 40 40 Z" />
      <path d="M60 36 L 72 34 L 72 40 L 60 40 Z" />
    </g>
  </svg>
);

const PalmLeaf = ({ style, rotate = 0, color = "#5F7A5A" }) => (
  <svg width="180" height="240" viewBox="0 0 180 240" style={{ position: "absolute", transform: `rotate(${rotate}deg)`, ...style }} aria-hidden="true">
    <g stroke={color} strokeWidth="2" fill="none" strokeLinecap="round">
      <path d="M 90 240 C 90 180, 90 120, 90 30" />
      {Array.from({ length: 9 }).map((_, i) => {
        const y = 40 + i * 22;
        const w = 60 - i * 4;
        return (
          <g key={i}>
            <path d={`M 90 ${y} C ${90 - w} ${y - 8}, ${90 - w - 6} ${y + 6}, ${90 - w - 10} ${y + 12}`} />
            <path d={`M 90 ${y} C ${90 + w} ${y - 8}, ${90 + w + 6} ${y + 6}, ${90 + w + 10} ${y + 12}`} />
          </g>
        );
      })}
    </g>
  </svg>
);

const Mushroom = ({ style, tint = "#B84A2A" }) => (
  <svg width="80" height="90" viewBox="0 0 80 90" style={{ position: "absolute", ...style }} aria-hidden="true">
    <path d="M12 40 C 12 20, 68 20, 68 40 L 62 44 L 18 44 Z" fill={tint} />
    <circle cx="28" cy="32" r="4" fill="#FAF8F5" />
    <circle cx="45" cy="26" r="3" fill="#FAF8F5" />
    <circle cx="55" cy="34" r="3.5" fill="#FAF8F5" />
    <path d="M28 44 L 26 82 L 54 82 L 52 44 Z" fill="#F2EFEB" stroke="#111" strokeWidth="1" />
  </svg>
);

// Small illustrated heart — hand-drawn feel, soft orange glow behind it.
const GlowHeart = ({ size = 22, color = "#FF6B2C" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <defs>
      <radialGradient id={`heartGlow-${color.replace("#", "")}`} cx="0.5" cy="0.5" r="0.6">
        <stop offset="0" stopColor={color} stopOpacity="0.55" />
        <stop offset="1" stopColor={color} stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx="12" cy="12" r="11" fill={`url(#heartGlow-${color.replace("#", "")})`} />
    <path
      d="M12 20 C 6 15, 3 12, 3 8.5 C 3 6, 5 4, 7.5 4 C 9.5 4, 11 5, 12 6.5 C 13 5, 14.5 4, 16.5 4 C 19 4, 21 6, 21 8.5 C 21 12, 18 15, 12 20 Z"
      fill={color}
      stroke={color}
      strokeWidth="0.8"
      strokeLinejoin="round"
    />
  </svg>
);

// Small palm silhouette — used to add secondary greenery without noise.
const SmallPalm = ({ color = "#5F7A5A", size = 90 }) => (
  <svg width={size} height={size * 1.4} viewBox="0 0 90 126" aria-hidden="true">
    <g stroke={color} strokeWidth="1.6" fill="none" strokeLinecap="round">
      <path d="M 45 126 C 45 90, 45 60, 45 18" />
      {Array.from({ length: 6 }).map((_, i) => {
        const y = 22 + i * 14;
        const w = 30 - i * 3;
        return (
          <g key={i}>
            <path d={`M 45 ${y} C ${45 - w} ${y - 4}, ${45 - w - 3} ${y + 3}, ${45 - w - 6} ${y + 6}`} />
            <path d={`M 45 ${y} C ${45 + w} ${y - 4}, ${45 + w + 3} ${y + 3}, ${45 + w + 6} ${y + 6}`} />
          </g>
        );
      })}
    </g>
  </svg>
);

// Shooting star — thin diagonal streak that fades. Animated via parent.
const ShootingStar = ({ length = 90, color = "#FF6B2C" }) => (
  <svg width={length + 24} height="24" viewBox={`0 0 ${length + 24} 24`} aria-hidden="true">
    <defs>
      <linearGradient id={`starTail-${color.replace("#", "")}`} x1="0" x2="1">
        <stop offset="0" stopColor={color} stopOpacity="0" />
        <stop offset="0.7" stopColor={color} stopOpacity="0.65" />
        <stop offset="1" stopColor={color} stopOpacity="1" />
      </linearGradient>
    </defs>
    <line
      x1="0"
      y1="12"
      x2={length}
      y2="12"
      stroke={`url(#starTail-${color.replace("#", "")})`}
      strokeWidth="1.2"
      strokeLinecap="round"
    />
    <circle cx={length} cy="12" r="3.2" fill={color} />
    <circle cx={length} cy="12" r="6" fill={color} opacity="0.25" />
  </svg>
);

// Butterfly — two wings, hand-illustrated, flap gently via a nested motion.
// The parent element handles the wandering flight path.
const Butterfly = ({ color = "#7BA6C2", accent = "#FFF7E8" }) => (
  <motion.svg
    width="36"
    height="30"
    viewBox="0 0 60 50"
    aria-hidden="true"
    animate={{ scaleX: [1, 0.35, 1] }}
    transition={{ duration: 0.42, repeat: Infinity, ease: "easeInOut" }}
    style={{ transformOrigin: "50% 50%" }}
  >
    {/* body */}
    <ellipse cx="30" cy="25" rx="1.6" ry="10" fill="#1a1a1a" />
    {/* head */}
    <circle cx="30" cy="14" r="2.2" fill="#1a1a1a" />
    {/* antennae */}
    <path d="M 30 12 C 28 8, 26 6, 24 5" stroke="#1a1a1a" strokeWidth="0.9" fill="none" strokeLinecap="round" />
    <path d="M 30 12 C 32 8, 34 6, 36 5" stroke="#1a1a1a" strokeWidth="0.9" fill="none" strokeLinecap="round" />
    <circle cx="24" cy="5" r="0.9" fill="#1a1a1a" />
    <circle cx="36" cy="5" r="0.9" fill="#1a1a1a" />
    {/* wings — left */}
    <path
      d="M 29 18 C 18 8, 6 14, 8 26 C 10 32, 18 30, 29 24 Z"
      fill={color}
      stroke="#1a1a1a"
      strokeWidth="0.9"
      strokeLinejoin="round"
    />
    <path
      d="M 29 26 C 16 30, 8 38, 14 44 C 22 46, 28 38, 29 32 Z"
      fill={color}
      stroke="#1a1a1a"
      strokeWidth="0.9"
      strokeLinejoin="round"
      opacity="0.92"
    />
    {/* wings — right */}
    <path
      d="M 31 18 C 42 8, 54 14, 52 26 C 50 32, 42 30, 31 24 Z"
      fill={color}
      stroke="#1a1a1a"
      strokeWidth="0.9"
      strokeLinejoin="round"
    />
    <path
      d="M 31 26 C 44 30, 52 38, 46 44 C 38 46, 32 38, 31 32 Z"
      fill={color}
      stroke="#1a1a1a"
      strokeWidth="0.9"
      strokeLinejoin="round"
      opacity="0.92"
    />
    {/* wing accent spots */}
    <circle cx="16" cy="22" r="1.6" fill={accent} opacity="0.85" />
    <circle cx="44" cy="22" r="1.6" fill={accent} opacity="0.85" />
    <circle cx="18" cy="36" r="1.2" fill={accent} opacity="0.75" />
    <circle cx="42" cy="36" r="1.2" fill={accent} opacity="0.75" />
  </motion.svg>
);

const LuggageTag = ({ style, rotate = 12 }) => (
  <svg width="120" height="160" viewBox="0 0 120 160" style={{ position: "absolute", transform: `rotate(${rotate}deg)`, ...style }} aria-hidden="true">
    <g stroke="#111111" strokeWidth="1.4" fill="#F2EFEB">
      <path d="M60 6 L 60 26" />
      <circle cx="60" cy="8" r="4" fill="none" />
      <rect x="20" y="28" width="80" height="118" rx="10" />
      <circle cx="60" cy="42" r="3" fill="#111" />
    </g>
    <text x="60" y="70" textAnchor="middle" fontFamily="Fraunces, serif" fontStyle="italic" fontSize="20" fill="#111">Michi</text>
    <text x="60" y="94" textAnchor="middle" fontFamily="Manrope" fontSize="9" fill="#111" letterSpacing="2">FROM BCN</text>
    <text x="60" y="112" textAnchor="middle" fontFamily="Manrope" fontSize="9" fill="#111" letterSpacing="2">TO EVERYWHERE</text>
    <path d="M32 128 L 108 128" stroke="#111" strokeWidth="1" />
    <text x="60" y="140" textAnchor="middle" fontFamily="Manrope" fontSize="8" fill="#111" letterSpacing="3">CLASS · A</text>
  </svg>
);

const DottedRoute = ({ d, style }) => (
  <svg width="100%" height="100%" viewBox="0 0 1600 900" preserveAspectRatio="none" style={{ position: "absolute", inset: 0, ...style }} aria-hidden="true">
    <path d={d} stroke="#111111" strokeWidth="1.4" strokeDasharray="1 6" strokeLinecap="round" fill="none" opacity="0.4" />
  </svg>
);

// The Michi character — elegant black cat with sunglasses, "iconic pose"
const Michi = ({ blink = false }) => (
  <svg viewBox="0 0 400 560" width="100%" height="100%" aria-label="Michi" role="img">
    <defs>
      <linearGradient id="furShine" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#1a1a1a" />
        <stop offset="1" stopColor="#050505" />
      </linearGradient>
    </defs>

    {/* Ground shadow */}
    <ellipse cx="200" cy="536" rx="132" ry="9" fill="#111" opacity="0.16" />

    {/* --- Tail (behind body, curling out to the right) --- */}
    <path
      d="M 288 470
         C 348 466, 372 420, 356 372
         C 348 348, 320 340, 300 352
         C 316 356, 328 372, 324 396
         C 320 420, 300 434, 280 434
         Z"
      fill="url(#furShine)"
    />

    {/* --- Body: sitting cat torso --- */}
    <path
      d="M 158 268
         C 128 292, 108 336, 104 384
         C 100 434, 116 490, 148 512
         C 162 522, 178 526, 200 526
         C 222 526, 238 522, 252 512
         C 284 490, 300 434, 296 384
         C 292 336, 272 292, 242 268
         Z"
      fill="url(#furShine)"
    />

    {/* Front paws */}
    <path d="M 168 506 C 158 506, 152 516, 154 526 L 190 526 C 192 516, 186 506, 178 506 Z" fill="#050505" />
    <path d="M 222 506 C 214 506, 208 516, 210 526 L 246 526 C 248 516, 242 506, 232 506 Z" fill="#050505" />

    {/* --- Head with two pointed ears (single closed silhouette) --- */}
    <path
      d="M 140 92
         L 172 168
         Q 186 158, 200 158
         Q 214 158, 228 168
         L 260 92
         L 278 172
         Q 292 220, 268 258
         Q 240 282, 200 284
         Q 160 282, 132 258
         Q 108 220, 122 172
         Z"
      fill="url(#furShine)"
    />

    {/* Ears inner tint */}
    <path d="M 150 118 L 168 158 L 158 162 Z" fill="#3a2620" opacity="0.75" />
    <path d="M 250 118 L 232 158 L 242 162 Z" fill="#3a2620" opacity="0.75" />

    {/* Whiskers */}
    <g stroke="#FAF8F5" strokeWidth="0.9" strokeLinecap="round" opacity="0.55">
      <path d="M 174 220 L 128 210" />
      <path d="M 174 228 L 124 226" />
      <path d="M 174 236 L 130 244" />
      <path d="M 226 220 L 272 210" />
      <path d="M 226 228 L 276 226" />
      <path d="M 226 236 L 270 244" />
    </g>

    {/* Sunglasses (aviator-ish) */}
    <g>
      <ellipse cx="170" cy="200" rx="22" ry="15" fill="#111" stroke="#FF6B2C" strokeWidth="1.6" />
      <ellipse cx="230" cy="200" rx="22" ry="15" fill="#111" stroke="#FF6B2C" strokeWidth="1.6" />
      <path d="M 190 200 Q 200 194, 210 200" stroke="#FF6B2C" strokeWidth="1.6" fill="none" />
      <path d="M 148 196 L 132 190" stroke="#FF6B2C" strokeWidth="1.6" />
      <path d="M 252 196 L 268 190" stroke="#FF6B2C" strokeWidth="1.6" />
      <ellipse cx="163" cy="196" rx="4.5" ry="2.2" fill="#FAF8F5" opacity="0.55" />
      <ellipse cx="223" cy="196" rx="4.5" ry="2.2" fill="#FAF8F5" opacity="0.55" />
      {blink && (
        <>
          <rect x="150" y="200" width="40" height="1.4" fill="#FAF8F5" opacity="0.65" />
          <rect x="210" y="200" width="40" height="1.4" fill="#FAF8F5" opacity="0.65" />
        </>
      )}
    </g>

    {/* Nose */}
    <path d="M 194 232 L 206 232 L 200 240 Z" fill="#FF6B2C" />
    {/* Mouth */}
    <path d="M 200 240 Q 196 250, 190 248" stroke="#FAF8F5" strokeWidth="1" fill="none" opacity="0.75" />
    <path d="M 200 240 Q 204 250, 210 248" stroke="#FAF8F5" strokeWidth="1" fill="none" opacity="0.75" />

    {/* Scarf */}
    <path
      d="M 158 274
         C 178 288, 222 288, 242 274
         L 250 306
         C 224 320, 176 320, 150 306
         Z"
      fill="#FF6B2C"
    />
    <path d="M 246 300 L 262 340 L 250 344 L 236 306 Z" fill="#FF6B2C" opacity="0.92" />

    {/* Suitcase held next to front paw */}
    <g transform="translate(60 408)">
      <rect x="0" y="14" width="58" height="40" rx="4" fill="#F2EFEB" stroke="#111" strokeWidth="1.4" />
      <rect x="20" y="6" width="18" height="10" rx="2" fill="none" stroke="#111" strokeWidth="1.4" />
      <rect x="4" y="24" width="50" height="3.5" fill="#FF6B2C" />
      <rect x="6" y="34" width="14" height="10" fill="none" stroke="#111" strokeWidth="1" />
      <text x="29" y="50" textAnchor="middle" fontFamily="Manrope" fontSize="6" fill="#111" letterSpacing="1">MICHI</text>
    </g>
  </svg>
);

// ---------- The Hero itself ----------

export default function IntroHero({ onEnter }) {
  const containerRef = useRef(null);
  const [blink, setBlink] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const btnRef = useRef(null);

  // Cat blink every 4-6s
  useEffect(() => {
    let t;
    const loop = () => {
      setBlink(true);
      setTimeout(() => setBlink(false), 160);
      t = setTimeout(loop, 4000 + Math.random() * 2500);
    };
    t = setTimeout(loop, 3000);
    return () => clearTimeout(t);
  }, []);

  // Cursor parallax
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const smx = useSpring(mx, { stiffness: 60, damping: 20, mass: 0.6 });
  const smy = useSpring(my, { stiffness: 60, damping: 20, mass: 0.6 });

  const catX = useTransform(smx, [-1, 1], [-18, 18]);
  const catY = useTransform(smy, [-1, 1], [-12, 12]);
  const bg1X = useTransform(smx, [-1, 1], [-35, 35]);
  const bg1Y = useTransform(smy, [-1, 1], [-20, 20]);
  const bg2X = useTransform(smx, [-1, 1], [25, -25]);
  const bg2Y = useTransform(smy, [-1, 1], [14, -14]);
  const paperX = useTransform(smx, [-1, 1], [-6, 6]);
  const paperY = useTransform(smy, [-1, 1], [-4, 4]);

  const handleMouse = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const ny = ((e.clientY - rect.top) / rect.height) * 2 - 1;
    mx.set(nx);
    my.set(ny);
  };

  const triggerEnter = () => {
    if (transitioning) return;
    setTransitioning(true);
    setTimeout(() => {
      try { sessionStorage.setItem("michi_intro_seen", "1"); } catch (e) { /* ignore */ }
      onEnter?.();
    }, 7200);
  };

  return (
    <div
      ref={containerRef}
      onMouseMove={handleMouse}
      className="fixed inset-0 z-[9500] overflow-hidden"
      style={{ background: "#FAF8F5" }}
      data-testid="intro-hero"
    >
      {/* Warm gradient wash */}
      <motion.div
        style={{ x: paperX, y: paperY }}
        className="absolute inset-0 pointer-events-none"
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(60% 50% at 30% 40%, rgba(255,107,44,0.14), transparent 60%), radial-gradient(50% 40% at 80% 70%, rgba(122,161,142,0.18), transparent 65%), radial-gradient(70% 55% at 60% 20%, rgba(232,196,164,0.28), transparent 60%)",
          }}
        />
      </motion.div>

      {/* Dotted travel routes */}
      <motion.div style={{ x: bg1X, y: bg1Y }} className="absolute inset-0 pointer-events-none">
        <DottedRoute d="M -50 700 C 300 500, 500 300, 900 220 C 1200 160, 1400 300, 1700 180" />
        <DottedRoute d="M 200 -50 C 300 200, 600 280, 900 500 C 1100 640, 1300 700, 1650 900" />
        <DottedRoute d="M 100 900 C 350 780, 500 700, 800 660 C 1000 640, 1200 720, 1600 640" />
      </motion.div>

      {/* Notebook grid overlay (subtle) */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.055]"
        style={{
          backgroundImage:
            "linear-gradient(#111 1px, transparent 1px), linear-gradient(90deg, #111 1px, transparent 1px)",
          backgroundSize: "42px 42px",
        }}
      />

      {/* Rainbow (top right) — with continuous subtle shimmer */}
      <motion.div
        style={{ x: bg2X, y: bg2Y }}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 0.8, y: 0 }}
        transition={{ delay: 0.9, duration: 1.6, ease: [0.76, 0, 0.24, 1] }}
        className="absolute top-16 right-8 lg:top-24 lg:right-24"
      >
        <motion.div
          animate={{ opacity: [0.75, 1, 0.75], scale: [1, 1.02, 1] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 2.4 }}
        >
          <Rainbow />
        </motion.div>
      </motion.div>

      {/* Palm leaves — with gentle sway */}
      <motion.div
        initial={{ opacity: 0, y: 60, rotate: -20 }}
        animate={{ opacity: 1, y: 0, rotate: -10 }}
        transition={{ delay: 0.4, duration: 1.4, ease: [0.76, 0, 0.24, 1] }}
        className="absolute -bottom-24 -left-12 lg:-bottom-16 lg:-left-6"
      >
        <motion.div
          animate={{ rotate: [-10, -6, -10] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1.8 }}
          style={{ transformOrigin: "50% 100%" }}
        >
          <PalmLeaf rotate={0} color="#3E5B3A" />
        </motion.div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 60, rotate: 25 }}
        animate={{ opacity: 1, y: 0, rotate: 14 }}
        transition={{ delay: 0.55, duration: 1.4, ease: [0.76, 0, 0.24, 1] }}
        className="absolute -bottom-24 -right-8 lg:-bottom-8 lg:-right-4"
      >
        <motion.div
          animate={{ rotate: [14, 10, 14] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 2.0 }}
          style={{ transformOrigin: "50% 100%" }}
        >
          <PalmLeaf rotate={0} color="#5F7A5A" />
        </motion.div>
      </motion.div>

      {/* Mushroom — moved to safe left corner, no longer clashes with CTA */}
      <motion.div
        initial={{ opacity: 0, scale: 0.5, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.9, ease: [0.34, 1.56, 0.64, 1] }}
        className="absolute bottom-16 left-[6%] hidden md:block"
      >
        <motion.div
          animate={{ y: [0, -6, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 2.1 }}
        >
          <Mushroom />
        </motion.div>
      </motion.div>

      {/* Passport stamps — with subtle continuous wobble */}
      <motion.div
        initial={{ opacity: 0, y: -20, rotate: -20 }}
        animate={{ opacity: 1, y: 0, rotate: -8 }}
        transition={{ delay: 1.0, duration: 1.2, ease: [0.76, 0, 0.24, 1] }}
        className="absolute top-24 left-10 hidden lg:block"
      >
        <motion.div
          animate={{ rotate: [-8, -10, -8], y: [0, -3, 0] }}
          transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: 2.2 }}
        >
          <PassportStamp label="BARCELONA · 41.3° N" rotate={0} />
        </motion.div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: -20, rotate: 20 }}
        animate={{ opacity: 1, y: 0, rotate: 6 }}
        transition={{ delay: 1.3, duration: 1.2, ease: [0.76, 0, 0.24, 1] }}
        className="absolute top-40 right-40 hidden lg:block"
      >
        <motion.div
          animate={{ rotate: [6, 8, 6], y: [0, -3, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 2.5 }}
        >
          <PassportStamp label="LISBOA · APPROVED" tint="#111111" rotate={0} />
        </motion.div>
      </motion.div>

      {/* Luggage tag — with swinging pendulum motion */}
      <motion.div
        initial={{ opacity: 0, y: -30, rotate: 30 }}
        animate={{ opacity: 1, y: 0, rotate: 12 }}
        transition={{ delay: 0.9, duration: 1.4, ease: [0.34, 1.56, 0.64, 1] }}
        className="absolute top-20 right-[26%] hidden lg:block"
      >
        <motion.div
          animate={{ rotate: [12, 8, 12, 15, 12] }}
          transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut", delay: 2.4 }}
          style={{ transformOrigin: "50% 5%" }}
        >
          <LuggageTag rotate={0} />
        </motion.div>
      </motion.div>

      {/* Airplane traversing */}
      <motion.div
        initial={{ x: "-10vw", y: 20, opacity: 0, rotate: -8 }}
        animate={{ x: "108vw", y: -30, opacity: 1, rotate: -8 }}
        transition={{ delay: 1.6, duration: 12, ease: "linear", repeat: Infinity, repeatDelay: 4 }}
        className="absolute top-[18%] left-0 hidden md:block"
      >
        <Airplane />
      </motion.div>

      {/* Paw prints trail (bottom) */}
      {[
        { l: "8%", b: "22%", r: 20, d: 1.4 },
        { l: "14%", b: "18%", r: 30, d: 1.55 },
        { l: "20%", b: "14%", r: 15, d: 1.7 },
        { l: "26%", b: "18%", r: 40, d: 1.85 },
        { l: "32%", b: "14%", r: 25, d: 2.0 },
        { l: "38%", b: "18%", r: 35, d: 2.15 },
      ].map((p, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 0.5, scale: 1 }}
          transition={{ delay: p.d, duration: 0.4 }}
          style={{ position: "absolute", left: p.l, bottom: p.b }}
        >
          <PawPrint rotate={p.r} size={26} opacity={0.55} />
        </motion.div>
      ))}

      {/* Handwritten labels */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 0.95, y: 0 }}
        transition={{ delay: 1.8, duration: 0.9 }}
        className="absolute top-[86px] left-[240px] hidden lg:flex items-center gap-3 -rotate-[4deg] pointer-events-none z-[12]"
        style={{ color: "#111" }}
      >
        <svg width="60" height="18" viewBox="0 0 60 18" fill="none" aria-hidden="true">
          <path d="M2 12 C 14 6, 30 4, 54 6" stroke="#111" strokeWidth="1.2" strokeDasharray="2 3" fill="none" strokeLinecap="round" />
          <path d="M50 2 L58 6 L52 12" stroke="#111" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="font-italic-display whitespace-nowrap" style={{ fontSize: "22px" }}>
          Barcelona → todas las paradas
        </span>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 0.8, y: 0 }}
        transition={{ delay: 2.0, duration: 0.9 }}
        className="absolute bottom-[24%] right-[8%] hidden lg:block font-italic-display rotate-[8deg] pointer-events-none z-[12]"
        style={{ fontSize: "20px", color: "#111" }}
      >
        cap 001 · el debut de Michi
      </motion.div>

      {/* Nav mini */}
      <div className="absolute top-0 left-0 right-0 z-10">
        <div className="max-w-[95%] mx-auto flex items-start justify-between py-6 font-sans text-[11px] tracking-[0.22em] uppercase text-[color:var(--ink)]/70">
          <div className="flex items-start gap-3">
            <CatMark size={30} stroke={1.4} color="#111" className="mt-[2px]" />
            <div className="flex flex-col leading-none">
              <span>La Michi Agencia</span>
              <span className="font-italic-display normal-case tracking-normal text-[13px] text-[color:var(--ink)]/70 mt-1">
                Agencia creativa
              </span>
            </div>
          </div>
          <div className="hidden md:block pt-[2px]" aria-hidden="true" />
          <button
            data-testid="intro-skip"
            onClick={triggerEnter}
            className="link-underline pt-[2px]"
          >
            Saltar intro
          </button>
        </div>
      </div>

      {/* Central composition */}
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        {/* Editorial tagline top */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 1.0, ease: [0.76, 0, 0.24, 1] }}
          className="flex flex-col items-center gap-2 mb-4"
        >
          <span className="font-sans text-[11px] tracking-[0.32em] uppercase text-[color:var(--ink)]/60">
            Un estudio · Un gato · Un manifiesto
          </span>
          <span className="font-sans text-[11px] tracking-[0.32em] uppercase text-[color:var(--ink)]/60">
            Cap. 001 · Presentación
          </span>
        </motion.div>

        {/* Big headline w/ Michi in middle */}
        <div className="flex items-center gap-4 lg:gap-8 relative">
          <motion.h1
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.9, duration: 1.1, ease: [0.76, 0, 0.24, 1] }}
            className="font-display text-[16vw] lg:text-[11vw] leading-[0.85] tightest"
          >
            La
          </motion.h1>
          <motion.div
            style={{ x: catX, y: catY }}
            initial={{ opacity: 0, y: 30, rotate: -3 }}
            animate={{ opacity: 1, y: 0, rotate: 0 }}
            transition={{ delay: 1.1, duration: 1.3, ease: [0.34, 1.56, 0.64, 1] }}
            className="relative w-[26vw] max-w-[240px] lg:max-w-[220px] aspect-[3/4] pointer-events-auto"
            data-cursor="hover"
          >
            <motion.div
              animate={{ y: [0, -6, 0], scale: [1, 1.012, 1] }}
              transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut", delay: 2.4 }}
              className="w-full h-full"
            >
              <Michi blink={blink} />
            </motion.div>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.0, duration: 1.1, ease: [0.76, 0, 0.24, 1] }}
            className="font-italic-display text-[16vw] lg:text-[11vw] leading-[0.85] tightest"
            style={{ color: "#FF6B2C" }}
          >
            Michi
          </motion.h1>
        </div>

        {/* "Agencia" — centered visual anchor under Michi (straight, enlarged) */}
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 1.0, ease: [0.76, 0, 0.24, 1] }}
          className="mt-2 lg:mt-2 relative flex flex-col items-center"
        >
          <span className="font-italic-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tightest pb-1" style={{ color: "#111" }}>
            Agencia
          </span>
          <span className="mt-3 font-sans text-[11px] tracking-[0.34em] uppercase text-[color:var(--ink)]/60">
            Creativa. Barcelona
          </span>
        </motion.div>

        {/* Subtitle — commercial positioning */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.55, duration: 1.0, ease: [0.76, 0, 0.24, 1] }}
          className="mt-6 lg:mt-10 max-w-xl text-center font-sans text-base lg:text-lg leading-relaxed text-[color:var(--ink)]/80 px-6"
        >
          Diseñamos webs con personalidad para negocios que quieren destacar.
        </motion.p>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.9, duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
          className="mt-8 lg:mt-14 pointer-events-auto"
        >
          <button
            ref={btnRef}
            onClick={triggerEnter}
            data-testid="intro-cta-discover"
            className="group relative inline-flex items-center gap-4 px-8 py-5 rounded-full border border-[color:var(--ink)] overflow-hidden"
            style={{ background: "transparent" }}
            data-cursor="hover"
          >
            <span className="absolute inset-0 rounded-full transition-transform duration-[900ms] ease-[cubic-bezier(0.76,0,0.24,1)] scale-x-0 origin-left group-hover:scale-x-100" style={{ background: "#FF6B2C" }} />
            <span className="relative z-[1] font-sans text-[13px] tracking-[0.14em] uppercase text-[color:var(--ink)] group-hover:text-[color:var(--bg)] transition-colors duration-500">
              Haz clic para descubrir
            </span>
            <span className="relative z-[1] inline-flex items-center justify-center w-8 h-8 rounded-full border border-[color:var(--ink)] group-hover:border-[color:var(--bg)] transition-colors duration-500">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M2 6 L 10 6 M 6 2 L 10 6 L 6 10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </button>
          <div
            className="mt-4 text-center font-italic-display text-lg"
            style={{ color: "#111", opacity: 0.65 }}
          >
            sin scroll · sólo curiosidad
          </div>
        </motion.div>
      </div>

      {/* Footer meta */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1.0 }}
        className="absolute bottom-0 left-0 right-0"
      >
        <div className="max-w-[95%] mx-auto flex items-baseline justify-end py-6 font-sans text-[11px] tracking-[0.22em] uppercase text-[color:var(--ink)]/60">
          <span>Escena 001 · Presentación</span>
        </div>
      </motion.div>

      {/* Floating decorative sparkles — subtle depth layer */}
      {[
        { l: "12%", t: "42%", delay: 2.4, size: 8, dur: 5.5 },
        { l: "88%", t: "58%", delay: 3.0, size: 6, dur: 6.2 },
        { l: "48%", t: "12%", delay: 2.8, size: 10, dur: 5.0 },
        { l: "72%", t: "20%", delay: 3.6, size: 7, dur: 6.8 },
        { l: "22%", t: "68%", delay: 3.2, size: 9, dur: 5.6 },
        { l: "62%", t: "78%", delay: 2.6, size: 6, dur: 5.9 },
      ].map((s, i) => (
        <motion.div
          key={`spark-${i}`}
          className="absolute pointer-events-none hidden md:block"
          style={{ left: s.l, top: s.t, width: s.size, height: s.size }}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: [0, 0.55, 0.55, 0], scale: [0, 1, 1, 0], rotate: [0, 90, 180, 270] }}
          transition={{ duration: s.dur, delay: s.delay, repeat: Infinity, repeatDelay: 1.2, ease: "easeInOut" }}
        >
          <svg viewBox="0 0 20 20" width="100%" height="100%" aria-hidden="true">
            <path d="M10 0 L11 8 L20 10 L11 12 L10 20 L9 12 L0 10 L9 8 Z" fill="#FF6B2C" opacity="0.75" />
          </svg>
        </motion.div>
      ))}

      {/* Second airplane — opposite direction with wobble */}
      <motion.div
        initial={{ x: "108vw", y: 0, opacity: 0, rotate: 172 }}
        animate={{ x: "-10vw", y: 40, opacity: [0, 1, 1, 0.6], rotate: 172 }}
        transition={{ delay: 5.0, duration: 16, ease: "linear", repeat: Infinity, repeatDelay: 8 }}
        className="absolute top-[64%] left-0 hidden lg:block opacity-70"
      >
        <Airplane />
      </motion.div>

      {/* Glowing hearts — small, hand-drawn, gentle pulse. Placed at safe corners. */}
      {[
        { l: "12%", t: "44%", size: 20, delay: 2.6, dur: 4.5 },
        { l: "86%", t: "62%", size: 24, delay: 3.2, dur: 5.2 },
        { l: "92%", t: "14%", size: 18, delay: 3.6, dur: 4.8 },
        { l: "5%", t: "36%", size: 16, delay: 4.0, dur: 5.5 },
      ].map((h, i) => (
        <motion.div
          key={`heart-${i}`}
          className="absolute pointer-events-none hidden md:block"
          style={{ left: h.l, top: h.t }}
          initial={{ opacity: 0, scale: 0 }}
          animate={{
            opacity: [0, 0.85, 0.5, 0.85, 0],
            scale: [0, 1, 0.9, 1.05, 0],
            y: [0, -6, -2, -10, -18],
          }}
          transition={{
            duration: h.dur,
            delay: h.delay,
            repeat: Infinity,
            repeatDelay: 1.6,
            ease: "easeInOut",
          }}
        >
          <GlowHeart size={h.size} color="#FF6B2C" />
        </motion.div>
      ))}

      {/* Shooting stars — diagonal streaks across the intro. */}
      {[
        { top: "20%", angle: 18, dur: 2.0, delay: 2.4, repeatDelay: 4.5 },
        { top: "48%", angle: 12, dur: 2.4, delay: 4.2, repeatDelay: 5.8 },
        { top: "70%", angle: -8, dur: 2.0, delay: 6.0, repeatDelay: 5.0 },
      ].map((s, i) => (
        <motion.div
          key={`shoot-${i}`}
          className="absolute pointer-events-none hidden lg:block left-0"
          style={{ top: s.top }}
          initial={{ x: "-15vw", opacity: 0 }}
          animate={{
            x: ["-15vw", "50vw", "120vw"],
            opacity: [0, 1, 0.9, 0],
          }}
          transition={{
            duration: s.dur,
            delay: s.delay,
            repeat: Infinity,
            repeatDelay: s.repeatDelay,
            ease: [0.22, 1, 0.36, 1],
            times: [0, 0.3, 0.6, 1],
          }}
        >
          <div style={{ transform: `rotate(${s.angle}deg)`, willChange: "transform" }}>
            <ShootingStar length={200} color="#FF6B2C" />
          </div>
        </motion.div>
      ))}

      {/* Small secondary palm — right-middle, subtle greenery pillar */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 0.55, y: 0 }}
        transition={{ delay: 1.6, duration: 1.2, ease: [0.76, 0, 0.24, 1] }}
        className="absolute right-[6%] bottom-[6%] hidden lg:block"
      >
        <motion.div
          animate={{ rotate: [-3, 2, -3] }}
          transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut", delay: 2.8 }}
          style={{ transformOrigin: "50% 100%" }}
        >
          <SmallPalm color="#3E5B3A" size={80} />
        </motion.div>
      </motion.div>

      {/* Small secondary palm — left top, thin silhouette */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 0.4, y: 0 }}
        transition={{ delay: 1.8, duration: 1.2, ease: [0.76, 0, 0.24, 1] }}
        className="absolute left-[4%] top-[10%] hidden lg:block"
      >
        <motion.div
          animate={{ rotate: [2, -3, 2] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 3.0 }}
          style={{ transformOrigin: "50% 100%" }}
        >
          <SmallPalm color="#5F7A5A" size={70} />
        </motion.div>
      </motion.div>

      {/* Butterflies — blue and yellow. Wander in gentle looping paths
          around the edges so they never overlap the central text. */}
      <motion.div
        className="absolute pointer-events-none hidden md:block z-[3]"
        initial={{ x: "8vw", y: "60vh", opacity: 0 }}
        animate={{
          x: ["8vw", "18vw", "12vw", "20vw", "8vw"],
          y: ["60vh", "50vh", "58vh", "48vh", "60vh"],
          opacity: [0, 1, 1, 1, 1],
          rotate: [0, -8, 6, -4, 0],
        }}
        transition={{
          duration: 16,
          delay: 2.2,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        <Butterfly color="#7BA6C2" accent="#EAF3F8" />
      </motion.div>

      <motion.div
        className="absolute pointer-events-none hidden md:block z-[3]"
        initial={{ x: "82vw", y: "35vh", opacity: 0 }}
        animate={{
          x: ["82vw", "74vw", "84vw", "70vw", "82vw"],
          y: ["35vh", "44vh", "38vh", "48vh", "35vh"],
          opacity: [0, 1, 1, 1, 1],
          rotate: [0, 6, -8, 4, 0],
        }}
        transition={{
          duration: 18,
          delay: 3.4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        <Butterfly color="#F4C55A" accent="#FFF7E8" />
      </motion.div>

      {/* Grain */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.06] mix-blend-multiply" style={{
        backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.9 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
      }} />

      {/* Living doodles — cycling in/out */}
      {!transitioning && <LivingDoodles />}
      {/* --- SIGNATURE TRANSITION: theatrical, ~4.8s --- */}
      <AnimatePresence>
        {transitioning && (
          <>
            {/* Soft dim over hero — deepens gradually so stamp reveal is a clean focal moment */}
            <motion.div
              key="soften"
              className="fixed inset-0 pointer-events-none"
              style={{ background: "#FAF8F5", zIndex: 9500 }}
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0.72, 0.97] }}
              transition={{ duration: 3.5, times: [0, 0.25, 1], ease: [0.76, 0, 0.24, 1] }}
            />

            {/* Dotted route drawing itself across the screen */}
            <motion.svg
              key="route"
              className="fixed inset-0 pointer-events-none"
              width="100%" height="100%" viewBox="0 0 1600 900"
              preserveAspectRatio="none"
              style={{ zIndex: 9600 }}
            >
              <motion.path
                d="M -20 720 C 260 620, 520 500, 780 460 C 1080 420, 1300 380, 1650 260"
                stroke="#111111"
                strokeWidth="2"
                strokeDasharray="2 8"
                strokeLinecap="round"
                fill="none"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 0.85 }}
                transition={{ duration: 2.4, delay: 0.9, ease: [0.76, 0, 0.24, 1] }}
              />
            </motion.svg>

            {/* Walking cat paws following the route */}
            {(() => {
              const paws = [
                { x: "6%", y: "78%", r: 12, s: 0.9 },
                { x: "14%", y: "72%", r: -10, s: 1.0 },
                { x: "22%", y: "66%", r: 14, s: 0.95 },
                { x: "30%", y: "60%", r: -12, s: 1.05 },
                { x: "38%", y: "56%", r: 8, s: 0.95 },
                { x: "46%", y: "52%", r: -14, s: 1.0 },
                { x: "54%", y: "48%", r: 10, s: 1.05 },
                { x: "62%", y: "44%", r: -10, s: 0.95 },
                { x: "70%", y: "40%", r: 12, s: 1.05 },
                { x: "78%", y: "36%", r: -8, s: 1.0 },
                { x: "86%", y: "32%", r: 10, s: 1.05 },
                { x: "94%", y: "28%", r: -12, s: 0.95 },
              ];
              return paws.map((p, i) => (
                <motion.div
                  key={`walk-paw-${i}`}
                  initial={{ opacity: 0, scale: 0.4, y: 8 }}
                  animate={{ opacity: [0, 1, 1, 0.5, 0], scale: [0.4, p.s, p.s, p.s * 0.95, p.s * 0.85], y: [8, 0, 0, 0, 0] }}
                  transition={{
                    duration: 3.6,
                    delay: 0.6 + i * 0.22,
                    times: [0, 0.15, 0.55, 0.8, 1],
                    ease: [0.76, 0, 0.24, 1],
                  }}
                  className="fixed"
                  style={{ left: p.x, top: p.y, zIndex: 9700 }}
                >
                  <svg width="40" height="40" viewBox="0 0 40 40" style={{ transform: `rotate(${p.r}deg)` }} aria-hidden="true">
                    <g fill="#111111">
                      <ellipse cx="10" cy="14" rx="3" ry="4" />
                      <ellipse cx="20" cy="10" rx="3" ry="4" />
                      <ellipse cx="30" cy="14" rx="3" ry="4" />
                      <ellipse cx="25" cy="21" rx="3" ry="4" />
                      <path d="M14 32 C 10 26, 30 26, 26 32 C 24 36, 16 36, 14 32 Z" />
                    </g>
                  </svg>
                </motion.div>
              ));
            })()}

            {/* Presenta / el destino — hold, then fade out before stamp holds */}
            <motion.div
              key="destino"
              className="fixed inset-0 flex items-center justify-center pointer-events-none"
              style={{ zIndex: 9750, color: "#111" }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: [0, 1, 1, 0], y: [20, 0, 0, -10] }}
              transition={{ duration: 3.2, delay: 0.7, times: [0, 0.18, 0.75, 1], ease: [0.76, 0, 0.24, 1] }}
            >
              <div className="text-center px-6">
                <div className="font-sans text-[11px] tracking-[0.36em] uppercase text-[color:var(--ink)]/70 mb-4 lg:mb-6">
                  Destino
                </div>
                <motion.div
                  className="font-italic-display text-[13vw] lg:text-[8vw] leading-[1.02] tightest pb-2"
                  animate={{ letterSpacing: ["-0.02em", "-0.01em", "-0.02em"] }}
                  transition={{ duration: 3.2, delay: 0.7, ease: "easeInOut" }}
                >
                  La Michi Agencia
                </motion.div>
                <div className="mt-6 lg:mt-8 font-sans text-[11px] tracking-[0.32em] uppercase text-[color:var(--ink)]/50">
                  → siguiendo las huellas de Michi
                </div>
              </div>
            </motion.div>

            {/* Passport stamp completion — appears at 3.2s, visible for ≥3s before wipe at 6.5s */}
            <motion.div
              key="stamp"
              className="fixed pointer-events-none"
              style={{ zIndex: 9770, left: "50%", top: "50%", translateX: "-50%", translateY: "-50%" }}
              initial={{ opacity: 0, scale: 0.4, rotate: -18 }}
              animate={{ opacity: [0, 1, 1, 1], scale: [0.4, 1.15, 1.0, 1.0], rotate: [-18, -6, -8, -8] }}
              transition={{ duration: 3.3, delay: 3.2, times: [0, 0.22, 0.32, 1], ease: [0.34, 1.56, 0.64, 1] }}
            >
              <div className="text-center">
                <svg width="520" height="260" viewBox="0 0 480 240" fill="none" aria-hidden="true" className="max-w-[92vw]">
                  <motion.rect
                    x="10" y="10" width="460" height="220" rx="14"
                    stroke="#FF6B2C" strokeWidth="3" fill="none"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.7, delay: 3.5, ease: [0.76, 0, 0.24, 1] }}
                  />
                  <rect x="22" y="22" width="436" height="196" rx="10" stroke="#FF6B2C" strokeWidth="1.8" strokeDasharray="6 5" />
                  <text x="240" y="72" textAnchor="middle" fontSize="14" fontFamily="Manrope, sans-serif" fill="#FF6B2C" letterSpacing="10">DESTINO</text>
                  <text x="240" y="152" textAnchor="middle" fontFamily="Fraunces, serif" fontStyle="italic" fontSize="56" fill="#FF6B2C">La Michi Agencia</text>
                  <text x="240" y="192" textAnchor="middle" fontSize="12" fontFamily="Manrope, sans-serif" fill="#FF6B2C" letterSpacing="6">ESCENA 001 · APPROVED</text>
                </svg>
                <motion.div
                  className="mt-6 font-sans text-[11px] tracking-[0.32em] uppercase"
                  style={{ color: "#FF6B2C" }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 0.85, 0.85, 0.85] }}
                  transition={{ duration: 3.3, delay: 3.6, times: [0, 0.15, 0.7, 1] }}
                >
                  Barcelona · 41.3° N — entrando al estudio
                </motion.div>
              </div>
            </motion.div>

            {/* Final wipe — cream sweep from top */}
            <motion.div
              key="wipe"
              className="fixed inset-0 pointer-events-none"
              style={{ background: "#FAF8F5", zIndex: 9800 }}
              initial={{ y: "-100%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 0.7, delay: 6.5, ease: [0.76, 0, 0.24, 1] }}
            />
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
